/**
 * Development Logging Service
 * Captures, manages, and reports development and runtime errors
 */

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  type: string;
  message: string;
  data?: any;
  stack?: string;
  component?: string;
}

interface DevLoggingConfig {
  maxLogs: number;
  enableConsole: boolean;
  enableNetwork: boolean;
  enableStorage: boolean;
  minLogLevel: 'debug' | 'info' | 'warn' | 'error';
}

class DevLoggingService {
  private logs: LogEntry[] = [];
  private config: DevLoggingConfig = {
    maxLogs: 500,
    enableConsole: true,
    enableNetwork: true,
    enableStorage: true,
    minLogLevel: 'debug',
  };

  private logLevels = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor() {
    this.initializeGlobalErrorHandler();
    this.initializeUnhandledRejectionHandler();
    this.exposeGlobalAPI();
  }

  /**
   * Create a new log entry
   */
  private createLogEntry(
    level: 'debug' | 'info' | 'warn' | 'error',
    type: string,
    message: string,
    data?: any
  ): LogEntry {
    return {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      level,
      type,
      message,
      data,
    };
  }

  /**
   * Add log to internal storage
   */
  private addLog(entry: LogEntry) {
    this.logs.push(entry);

    // Trim logs if exceeding max
    if (this.logs.length > this.config.maxLogs) {
      this.logs = this.logs.slice(-this.config.maxLogs);
    }

    // Also log to console in development
    if (this.config.enableConsole && import.meta.env.DEV) {
      this.logToConsole(entry);
    }
  }

  /**
   * Log to browser console with styling
   */
  private logToConsole(entry: LogEntry) {
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const prefix = `[${timestamp}] [${entry.type}]`;

    const styles = {
      debug: 'color: #888; font-size: 12px;',
      info: 'color: #0066cc; font-weight: bold;',
      warn: 'color: #ff9900; font-weight: bold;',
      error: 'color: #cc0000; font-weight: bold;',
    };

    console.log(`%c${prefix} ${entry.message}`, styles[entry.level], entry.data || '');
  }

  /**
   * Handle global errors
   */
  private initializeGlobalErrorHandler() {
    window.addEventListener('error', (event) => {
      const entry = this.createLogEntry(
        'error',
        'RUNTIME_ERROR',
        event.message,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error?.toString(),
          stack: event.error?.stack,
        }
      );

      this.addLog(entry);
      this.sendToBackendIfEnabled(entry);
    });
  }

  /**
   * Handle unhandled promise rejections
   */
  private initializeUnhandledRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
      const entry = this.createLogEntry(
        'error',
        'UNHANDLED_REJECTION',
        event.reason?.message || String(event.reason),
        {
          reason: event.reason,
          stack: event.reason?.stack,
        }
      );

      this.addLog(entry);
      this.sendToBackendIfEnabled(entry);
    });
  }

  /**
   * Log debug message
   */
  debug(type: string, message: string, data?: any) {
    if (this.logLevels.debug >= this.logLevels[this.config.minLogLevel]) {
      const entry = this.createLogEntry('debug', type, message, data);
      this.addLog(entry);
    }
  }

  /**
   * Log info message
   */
  info(type: string, message: string, data?: any) {
    if (this.logLevels.info >= this.logLevels[this.config.minLogLevel]) {
      const entry = this.createLogEntry('info', type, message, data);
      this.addLog(entry);
    }
  }

  /**
   * Log warning message
   */
  warn(type: string, message: string, data?: any) {
    if (this.logLevels.warn >= this.logLevels[this.config.minLogLevel]) {
      const entry = this.createLogEntry('warn', type, message, data);
      this.addLog(entry);
    }
  }

  /**
   * Log error message
   */
  error(type: string, message: string, data?: any) {
    if (this.logLevels.error >= this.logLevels[this.config.minLogLevel]) {
      const entry = this.createLogEntry('error', type, message, data);
      this.addLog(entry);
      this.sendToBackendIfEnabled(entry);
    }
  }

  /**
   * Log component error (from error boundary)
   */
  logComponentError(component: string, error: Error, errorInfo: any) {
    const entry = this.createLogEntry('error', 'COMPONENT_ERROR', error.message, {
      component,
      componentStack: errorInfo.componentStack,
      stack: error.stack,
    });

    entry.component = component;
    this.addLog(entry);
    this.sendToBackendIfEnabled(entry);
  }

  /**
   * Send error to backend for aggregation
   */
  private sendToBackendIfEnabled(entry: LogEntry) {
    if (!this.config.enableNetwork || entry.level !== 'error') {
      return;
    }

    // Non-blocking fetch to avoid impacting app performance
    const payload = {
      level: entry.level,
      type: entry.type,
      message: entry.message,
      component: entry.component,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: entry.timestamp,
      data: entry.data,
    };

    const apiBaseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5001');
    fetch(`${apiBaseUrl}/shared/log-frontend-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Silently fail if logging endpoint is unavailable
    });
  }

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Filter logs by level
   */
  getLogsByLevel(level: 'debug' | 'info' | 'warn' | 'error'): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }

  /**
   * Filter logs by type
   */
  getLogsByType(type: string): LogEntry[] {
    return this.logs.filter((log) => log.type === type);
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Download logs as file
   */
  downloadLogs() {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Expose global API for browser console access
   */
  private exposeGlobalAPI() {
    window.__devLogging = {
      getLogs: () => this.getLogs(),
      getLogsByLevel: (level: any) => this.getLogsByLevel(level),
      getLogsByType: (type: string) => this.getLogsByType(type),
      clearLogs: () => this.clearLogs(),
      exportLogs: () => this.exportLogs(),
      downloadLogs: () => this.downloadLogs(),
      logError: (message: string, data?: any) => this.error('MANUAL', message, data),
      logEvent: (type: string, message: string, data?: any) => this.info(type, message, data),
    };

    if (import.meta.env.DEV) {
      console.log('%c✔ Dev Logging Service Ready', 'color: green; font-weight: bold;');
      console.log('%cAccess logs via: window.__devLogging', 'color: blue;');
      console.log('Available methods:', [
        'getLogs()',
        'getLogsByLevel(level)',
        'getLogsByType(type)',
        'clearLogs()',
        'exportLogs()',
        'downloadLogs()',
        'logError(message, data)',
        'logEvent(type, message, data)',
      ]);
    }
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<DevLoggingConfig>) {
    this.config = { ...this.config, ...config };
  }
}

// Create singleton instance
export const devLogging = new DevLoggingService();

// Export for direct use
export default devLogging;

// Augment Window type
declare global {
  interface Window {
    __devLogging?: {
      getLogs: () => LogEntry[];
      getLogsByLevel: (level: string) => LogEntry[];
      getLogsByType: (type: string) => LogEntry[];
      clearLogs: () => void;
      exportLogs: () => string;
      downloadLogs: () => void;
      logError: (message: string, data?: any) => void;
      logEvent: (type: string, message: string, data?: any) => void;
    };
  }
}
